import { MainPlayer } from '@stores/krpPlayerStores';
import {
  type STImage,
  type STImageMarker,
  type STMatrix,
  type SceneData,
  SceneType,
} from '@types';
import classNames from 'classnames';
import { useEffect } from 'react';
import { stSceneKrpPlayer } from '../../../sandTable/STKrpPlayer';
import { isSTMarkerScene } from '../../../sandTable/utils/markerTypeCheck';
import { onPlayerViewChange } from '../../actions/viewActions';

interface Props {
  data: STImage | STMatrix | null;
  scene: SceneData;
}

const PLAYER_ID = 'scene_st_krp';

export function MatrixContainer({ data, scene }: Props) {
  const north = scene.north || 0;

  const getActiveMarker = (): STImageMarker | undefined => {
    if (!data || scene.type !== SceneType.pano) return;
    return data.markers.find(
      (x) => isSTMarkerScene(x) && x.data.sceneId === scene.id
    );
  };

  useEffect(() => {
    if (getActiveMarker()) {
      return onPlayerViewChange.watch((view) => {
        changeRadarAngle(view.hlookat);
        stSceneKrpPlayer.updateRadarFov(view.fov);
      });
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      stSceneKrpPlayer.create({
        target: PLAYER_ID,
        data,
        radarSize: 35,
        ...(getActiveMarker() && {
          onRadarRotate: (angle) => {
            MainPlayer.get()?.updateView({ hlookat: angle + north });
          },
        }),
        onReady: handleReady,
      });
    }
    return () => {
      stSceneKrpPlayer.remove();
    };
  }, [data]);

  const handleReady = () => {
    if (!data) return;
    const markers = data.showCurrentSceneOnly
      ? data.markers.filter(
          (x) => isSTMarkerScene(x) && x.data.sceneId === scene.id
        )
      : data.markers;
    markers.forEach((marker) => {
      stSceneKrpPlayer.addMarker(marker, {
        dragable: false,
        markerSize: 14,
        hideTitle: true,
      });
    });
    const marker = getActiveMarker();
    if (marker) {
      stSceneKrpPlayer.addMarker(marker, {
        dragable: false,
        isActive: true,
        zOrder: 300,
        markerSize: 14,
        hideTitle: true,
      });
      changeRadarAngle(MainPlayer.get()?.getView()?.hlookat);
      if (isSTMarkerScene(marker) && marker.icon.showRadar) {
        stSceneKrpPlayer.showRadarOnMarker(marker.id);
        stSceneKrpPlayer.updateRadarFov(MainPlayer.get()?.getView()?.fov);
      }
    }
  };

  const changeRadarAngle = (ath = 0) => {
    stSceneKrpPlayer.updateAngle((ath % 360) - north);
  };

  return (
    <div
      className={classNames('absolute inset-0', { hidden: !data })}
      id={PLAYER_ID}
    />
  );
}
